import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAdminSession } from "../../services/auth/me";

/**
 * Guard wrapping every authenticated admin route.
 *
 * - No admin session → kick to `/login` on the admin domain (NOT to the
 *   consumer-app login surface). The redirect query param round-trips
 *   the original location so we land back where they intended.
 * - 2FA enrolled but not yet verified for this session → /verify-otp.
 *   The server returns 401 from `/me` in that case (because the strategy
 *   refuses tokens without `isTwoFactorAuth: true`), which collapses to
 *   "no user" here. The login flow already routes to /verify-otp, so
 *   this branch only matters when someone deep-links into /campaigns
 *   between login and OTP — same redirect, no special-case.
 */
export const AdminAuthGuard = () => {
  const location = useLocation();
  const { user, loading } = useAdminSession();

  if (loading) return null;

  if (!user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate replace to={`/login?redirect=${redirect}`} />;
  }

  // Defence-in-depth: server-side guards already 403 non-admins from every
  // /api/admin/* call, but rendering the chrome at all leaks the existence
  // of admin features. Bail to /login if something somehow returned a
  // non-admin.
  if (user.role !== "admin") {
    return <Navigate replace to="/login" />;
  }

  return <Outlet />;
};
